import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';
import { MdDragIndicator } from 'react-icons/md';
import {
  LoadingStrategyOption,
  RecommendationType,
} from '@/webapi/use-widget-catalog-api';
import { StrategySelection } from '@/features/editor/widgets/custom-widget/loading-section/shared/strategy-select';
import { StrategyPerSlotContext } from '@/features/editor/widgets/custom-widget/loading-section/strategy-per-slot/context/context';
import { LayeredRulingContext } from '@/features/editor/widgets/custom-widget/loading-section/layered-ruling/context';
import { SlotNum } from '@/features/editor/widgets/custom-widget/loading-section/shared/product-tile';
import { DuplicateSlot } from '@/features/editor/widgets/custom-widget/loading-section/shared/duplicate-slot';
import { SlotState } from '@/features/editor/widgets/custom-widget/loading-section/shared/models';

export const SlotContainer = styled.div`
  height: 100%;
  min-width: 33rem;
  min-height: 100%;
  background-color: #f5f5f5;
  display: flex;
  flex-direction: column;
  justify-content: center;
  border-radius: 8px;
  padding: 4% 4% 2.5%;
  position: relative;
  ${(p) =>
    p.isClickable
      ? `
    :hover {
    opacity: 0.5;
    cursor: pointer;
  }
  :active {
    opacity: 1;
    cursor: default;
  }
  `
      : ``}
  && > svg.dragIcon {
    position: absolute;
    bottom: 10%;
    right: 5%;
  }
  && > svg.dragIcon:focus {
    outline: none;
  }
  && > svg.dragIcon:hover * {
    color: black;
  }
`;

type Props = {
  slotNum: number;
  onProductSelect: () => void;
  onStrategySelect: (value: LoadingStrategyOption) => () => void;
  onDuplicateSelect: (value: number) => () => void;
  draggableAttributes?: any;
  draggableListeners?: any;
  index?: number;
  slots?: SlotState[];
};

export function EmptySlot({
  slotNum,
  onProductSelect,
  onStrategySelect,
  onDuplicateSelect,
  draggableAttributes,
  draggableListeners,
  index,
  slots,
}: Props) {
  const sCtx = useContext(StrategyPerSlotContext);
  const lCtx = useContext(LayeredRulingContext);
  const isPostPurchase = sCtx?.isPostPurchase || lCtx?.isPostPurchase;

  if (isPostPurchase) {
    return (
      <SlotContainer onClick={onProductSelect} isClickable={isPostPurchase}>
        <span>SLOT {slotNum + 1}</span>
        <span style={{ lineHeight: `4rem` }}>Click to select a product</span>
      </SlotContainer>
    );
  }
  const filteredIndexes = useMemo(
    () =>
      slots
        ? slots
            .map((slot, index) =>
              slot.filter &&
              ((slot.strategy && slot.strategy !== RecommendationType.MANUAL) ||
                slot.manualProduct)
                ? index
                : -1,
            )
            .filter((index) => index !== -1)
        : [],
    [slots],
  );

  return (
    <SlotContainer>
      {slotNum >= 0 && <SlotNum>{index + 1}</SlotNum>}
      <ButtonWrapper>
        <Title>EMPTY SLOT</Title>
        <ActionsWrapper>
          <StrategySelection onSelect={onStrategySelect} />
          <button type="button" onClick={onProductSelect}>
            Select Product
          </button>
          {filteredIndexes?.length > 0 && (
            <DuplicateSlot
              filteredIndexes={filteredIndexes}
              onSelect={onDuplicateSelect}
            />
          )}
        </ActionsWrapper>
      </ButtonWrapper>
      <MdDragIndicator
        {...draggableAttributes}
        {...draggableListeners}
        className="dragIcon"
        size={25}
        cursor="grab"
        color="#798794"
      />
    </SlotContainer>
  );
}
const Title = styled.div`
  font-family: 'Inter', serif;
  font-size: 1.5rem;
  font-weight: 600;
  color: #000;
  margin-bottom: 10px;
  width: 100%;
`;

const ActionsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 12px;
  align-content: center;
  flex: 1;
`;

const ButtonWrapper = styled.div`
  width: calc(100% - 20px);
  max-height: fit-content;
  margin: 2rem 0 2rem 2rem;
  aspect-ratio: 2.5;
  font-size: 1.2rem;
  font-family: 'JetBrains Mono', serif;
  font-weight: normal;
  color: #7e8993;
  display: flex;
  flex-direction: column;
  button {
    font-family: 'Inter', serif;
    font-size: 1.2rem;
    font-weight: 500;
    color: #000;
    padding: 10px 12px;
    border-radius: 5px;
    box-shadow: 0 4px 10px 0 rgba(0, 0, 0, 0),
      0 7px 10px 0 rgba(151, 210, 206, 0.22), 5px 1px 10px 0 rgba(0, 0, 0, 0.07);
    border: solid 1px rgb(197 200 203 / 36%);
    background-color: #fff;
    &:active {
      cursor: default;
      opacity: 1;
    }
    &:hover {
      cursor: pointer;
      opacity: 0.5;
    }
  }
`;
